import { useState } from "react";
import { GRAD } from "../../../Constants";
import styles from "./SpotDashboard.module.css";
export function LineChart({ labels, data, gradKey = "sky", monthChartData = [], monthCounts }) {
  const [hovered, setHovered] = useState(null);
  if (!data.length) {
    return <div className={styles["sd-empty-chart"]}>No data Available</div>;
  }

  const lineColor = GRAD[gradKey][0];
  const W = 320;
  const H = 220;
  const PL = 36;
  const PR = 14;
  const PT = 16;
  const PB = 30;
  const chartW = W - PL - PR;
  const chartH = H - PT - PB;

  const numericData = data.map(item => Number(item || 0));
  const dataMin = Math.min(...numericData);
  const dataMax = Math.max(...numericData, 1);
  const axisMin = Math.max(0, dataMin - 1);
  const axisMax = dataMax + 1;
  const axisRange = axisMax - axisMin || 1;
  const step = Math.ceil(axisRange / 5) || 1;

  const ticks = [];
  for (let value = axisMin; value <= axisMax + 0.0001; value += step) {
    ticks.push(Math.round(value * 100) / 100);
  }

  const toY = value => PT + (1 - (value - axisMin) / axisRange) * chartH;
  const toX = index => PL + (index / (labels.length - 1 || 1)) * chartW;

  const points = numericData.map((value, index) => ({
    x: toX(index),
    y: toY(value),
    value,
  }));

  const polyline = points.map(point => `${point.x},${point.y}`).join(" ");
  const areaPath =
    `M${points[0].x} ${points[0].y}` +
    points.slice(1).map(point => ` L${point.x} ${point.y}`).join("") +
    ` L${points[points.length - 1].x} ${H - PB} L${points[0].x} ${H - PB} Z`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className={styles["sd-line-svg"]}>
      <defs>
        <linearGradient id="sdLineAreaGradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={lineColor} stopOpacity="0.18" />
          <stop offset="100%" stopColor={lineColor} stopOpacity="0.02" />
        </linearGradient>
      </defs>

      {ticks.map((tick, index) => {
        const y = toY(tick);

        if (y < PT - 2 || y > H - PB + 2) return null;

        return (
          <g key={index}>
            <line x1={PL} y1={y} x2={W - PR} y2={y} stroke="#ECEDF8" strokeWidth="1" />
            <text x={PL - 5} y={y} textAnchor="end" dominantBaseline="middle" fontSize="9" fill="#727271">
              {tick}
            </text>
          </g>
        );
      })}

      <path d={areaPath} fill="url(#sdLineAreaGradient)" />
      <polyline
        points={polyline}
        fill="none"
        stroke={lineColor}
        strokeWidth="2.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />

      {points.map((point, index) => (
        <g
          key={index}
          onMouseEnter={() => setHovered(index)}
          onMouseLeave={() => setHovered(null)}
          style={{ cursor: "pointer" }}
        >
          <circle
            cx={point.x}
            cy={point.y}
            r="4"
            fill="#FEFEFE"
            stroke={lineColor}
            strokeWidth="2.5"
          />

          <circle
            cx={point.x}
            cy={point.y}
            r="14"
            fill="transparent"
          />

          {point.value > 0 && (
            <text
              x={point.x}
              y={point.y - 8}
              textAnchor="middle"
              fontSize="9"
              fontWeight="700"
              fill={lineColor}
            >
              {point.value}
            </text>
          )}
        </g>
      ))}
      {hovered !== null && points[hovered] && (
        <g className={styles["sd-svg-tooltip"]}>
          {(() => {
            const point = points[hovered];
            const weekData = monthChartData[hovered] || {};

            const tooltipWidth = 95;
            const tooltipHeight = 82;

            const tooltipX =
              point.x + tooltipWidth > W
                ? point.x - tooltipWidth - 8
                : point.x + 8;

            const tooltipY =
              point.y - tooltipHeight < 0
                ? point.y + 10
                : point.y - tooltipHeight - 8;

            return (
              <>
                <rect
                  x={tooltipX}
                  y={tooltipY}
                  width={tooltipWidth}
                  height={tooltipHeight}
                  rx="7"
                  fill="#FEFEFE"
                  stroke={lineColor}
                />

                <text
                  x={tooltipX + tooltipWidth / 2}
                  y={tooltipY + 14}
                  textAnchor="middle"
                  fontSize="9"
                  fontWeight="700"
                  fill="#2B2A29"
                >
                  {labels[hovered]} total: {point.value}
                </text>

                <text x={tooltipX + 10} y={tooltipY + 30} fontSize="8.5" fill="#727271">
                  Week 1: {Number(weekData.count_week_1 || 0)}
                </text>
                <text x={tooltipX + 10} y={tooltipY + 42} fontSize="8.5" fill="#727271">
                  Week 2: {Number(weekData.count_week_2 || 0)}
                </text>
                <text x={tooltipX + 10} y={tooltipY + 54} fontSize="8.5" fill="#727271">
                  Week 3: {Number(weekData.count_week_3 || 0)}
                </text>
                <text x={tooltipX + 10} y={tooltipY + 66} fontSize="8.5" fill="#727271">
                  Week 4: {Number(weekData.count_week_4 || 0)}
                </text>
                <text x={tooltipX + 10} y={tooltipY + 78} fontSize="8.5" fill="#727271">
                  Week 5: {Number(weekData.count_week_5 || 0)}
                </text>
              </>
            );
          })()}
        </g>
      )}
      {labels.map((label, index) => (
        <text key={index} x={toX(index)} y={H - PB + 11} textAnchor="middle" fontSize="9" fill="#727271">
          {label}
        </text>
      ))}

      <line x1={PL} y1={PT} x2={PL} y2={H - PB} stroke="#ECEDF8" strokeWidth="1" />
    </svg>
  );
}